import { useMemo, useState } from 'react';
import { Loader2, Plus, Server, Trash, Pencil } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import EmptyView from '@/shared/components/EmptyView';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import { MoreVertical } from 'lucide-react';
import { useAccountServiceAccounts, useRemoveServiceAccountFromOrganization } from '@/domains/service-accounts/services/account-service-accounts';
import { Card, CardContent } from '@/shared/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/ui/dropdown-menu';
import { ServiceAccount } from '@/shared/types/serviceAccountTypes';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import CreateServiceAccountDialog from '@/domains/service-accounts/dialogs/CreateServiceAccountDialog';
import ManageServiceAccountDialog from '@/domains/service-accounts/dialogs/ManageServiceAccountDialog';
import SearchBar from '@/shared/components/SearchBar';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export const ServiceAccountsSection = () => {
  const { hasPermission } = usePermissions();

  const canManageServiceAccounts = hasPermission(OrgType.TENANT, Feature.SERVICE_ACCOUNTS, Permission.MANAGE);

  // Fetch service accounts data
  const { data: serviceAccounts, isLoading: isLoadingServiceAccounts, error: serviceAccountsError, refetch: refetchServiceAccounts } = useAccountServiceAccounts();

  // Selected Service Account
  const [selectedServiceAccount, setSelectedServiceAccount] = useState<ServiceAccount | null>(null);

  // Add Service Account Dialog state
  const [isAddServiceAccountDialogOpen, setIsAddServiceAccountDialogOpen] = useState(false);

  // Manage Service Account Dialog states
  const [isManageServiceAccountDialogOpen, setIsManageServiceAccountDialogOpen] = useState(false);

  // Remove User Dialog states
  const [isRemoveServiceAccountDialogOpen, setIsRemoveServiceAccountDialogOpen] = useState(false);
  const removeServiceAccountFromOrganization = useRemoveServiceAccountFromOrganization();

  // Search Term
  const [searchTerm, setSearchTerm] = useState('');

  // Filter service accounts based on search term
  const filteredServiceAccounts = useMemo(() => {
    return serviceAccounts?.filter(serviceAccount =>
      serviceAccount.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [serviceAccounts, searchTerm]);

  // Handle Add Service Account
  const handleAddServiceAccount = () => {
    setIsAddServiceAccountDialogOpen(true);
  };

  // Handle Manage Service Account
  const onManageServiceAccount = (serviceAccount: ServiceAccount) => {
    setSelectedServiceAccount(serviceAccount);
    setIsManageServiceAccountDialogOpen(true);
  };

  // Handle Remove Service Account
  const onRemoveServiceAccount = (serviceAccount: ServiceAccount) => {
    setSelectedServiceAccount(serviceAccount);
    setIsRemoveServiceAccountDialogOpen(true);
  };

  // Called when user confirms in alert dialog
  const confirmRemoveServiceAccount = async () => {
    if (!selectedServiceAccount) return;
    try {
      await removeServiceAccountFromOrganization.mutateAsync({ serviceAccountId: selectedServiceAccount.clientId });
      showSuccess(SUCCESS.SERVICE_ACCOUNT.REMOVED);
      refetchServiceAccounts();
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_ACCOUNT.REMOVE);
    } finally {
      setIsRemoveServiceAccountDialogOpen(false);
      setSelectedServiceAccount(null);
    }
  };

  return (
    <div key="service-accounts" className="space-y-4">
      {/* Service Accounts Section Header */}
      {isLoadingServiceAccounts ? (
        <div className="flex justify-center items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading service accounts...</p>
        </div>
      ) : (
        <>
          <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
            <div>
              <h2 className="text-2xl font-bold">Service Accounts</h2>
              <span className="text-sm">Manage service accounts to access APIs for your account</span>
            </div>
            {canManageServiceAccounts && (
              <Button
                variant="default"
                className="gap-2"
                onClick={handleAddServiceAccount}
                disabled={isLoadingServiceAccounts}
              >
                <Plus className="h-4 w-4" />
                Add Service Account
              </Button>
            )}
          </div>

          {serviceAccounts && serviceAccounts.length > 0 && !serviceAccountsError ? (
            <div className="space-y-4">
              {/* Search Section */}
              <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search service accounts by name..." />

              {/* Service Accounts Table */}
              {filteredServiceAccounts && filteredServiceAccounts.length > 0 ? (
                <Card>
                  <CardContent className="p-0">
                    <Table key="service-accounts-table" className="rounded-lg overflow-hidden">
                      <TableHeader>
                        <TableRow className="bg-muted">
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">Name</TableHead>
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">ID</TableHead>
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">Status</TableHead>
                          <TableHead className="text-left py-3 px-4 font-medium font-bold"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredServiceAccounts.map((serviceAccount) => {
                          const canManageServiceAccount = canManageServiceAccounts;
                          return (
                            <TableRow key={serviceAccount.clientId} className="border-b hover:bg-muted/50">
                              <TableCell className="py-3 px-4 font-medium">
                                <div className="flex flex-col items-start gap-1">
                                  <p className="text-sm font-medium">{serviceAccount.name}</p>
                                </div>
                              </TableCell>
                              <TableCell className="py-4">
                                <p className="text-sm">{serviceAccount.clientId}</p>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge variant={serviceAccount.status ? 'default' : 'destructive'} className="text-xs">
                                  {serviceAccount.status ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-4 text-right">
                                {canManageServiceAccount && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {onManageServiceAccount && (
                                        <DropdownMenuItem onClick={(e) => {
                                          e.stopPropagation();
                                          onManageServiceAccount(serviceAccount);
                                        }}>
                                          <Pencil className="mr-2 h-4 w-4" />
                                          Manage
                                        </DropdownMenuItem>
                                      )}
                                      {onRemoveServiceAccount && (
                                        <DropdownMenuItem
                                          className="text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveServiceAccount(serviceAccount);
                                          }}
                                        >
                                          <Trash className="mr-2 h-4 w-4" />
                                          Remove
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <EmptyView
                  icon={Server}
                  title="No Service Accounts Found"
                  description="There are no service accounts available."
                />
              )}
            </div>
          ) : (
            <EmptyView
              icon={Server}
              title="No Service Accounts Found"
              description="There are no service accounts available."
            />
          )}
        </>
      )}

      {/* Add Service Account Dialog */}
      <CreateServiceAccountDialog
        isOpen={isAddServiceAccountDialogOpen}
        onClose={() => {
          setIsAddServiceAccountDialogOpen(false);
          refetchServiceAccounts();
        }}
      />

      {/* Manage Service Account Dialog */}
      <ManageServiceAccountDialog
        key={isManageServiceAccountDialogOpen && selectedServiceAccount ? selectedServiceAccount.clientId : 'closed'}
        isOpen={isManageServiceAccountDialogOpen}
        onClose={() => {
          setIsManageServiceAccountDialogOpen(false);
          setSelectedServiceAccount(null);
        }}
        onSuccess={() => refetchServiceAccounts()}
        serviceAccount={selectedServiceAccount}
      />

      {/* Remove Service Account Dialog */}
      <GenericAlertDialog
        open={isRemoveServiceAccountDialogOpen}
        onOpenChange={setIsRemoveServiceAccountDialogOpen}
        title="Remove Service Account"
        description="Are you sure you want to remove this service account?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={confirmRemoveServiceAccount}
        loading={removeServiceAccountFromOrganization.isPending}
      />
    </div>
  );
}
